import { useState, useCallback, useMemo } from 'react';
import {
  DataTable,
  type ColumnDef,
  type SortState,
  type PaginationState,
} from '@/components/data-table';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import { JobSearchModal, type JobSearchResult } from '@/components/JobSearchModal';
import {
  useWipLedger,
  useWriteUp,
  useWriteDown,
  useRealization,
  useStaffProfitability,
  useClientProfitability,
  type WipEntry,
  type WipEntryType,
  type WipLedgerParams,
  type StaffProfitability,
  type ClientProfitability,
} from './hooks/useWip';
import { useTeams } from './hooks/useTeams';

// ─── Constants ──────────────────────────────────────────────────────────────

const ENTRY_TYPE_BADGE: Record<
  WipEntryType,
  { variant: 'default' | 'info' | 'warning' | 'success' | 'danger' | 'outline'; label: string }
> = {
  time: { variant: 'info', label: 'Time' },
  write_up: { variant: 'success', label: 'Write-Up' },
  write_down: { variant: 'danger', label: 'Write-Down' },
  invoice: { variant: 'warning', label: 'Invoice' },
  reversal: { variant: 'outline', label: 'Reversal' },
  credit_note: { variant: 'success', label: 'Credit Note' },
  write_off: { variant: 'danger', label: 'Write-Off' },
  discount: { variant: 'warning', label: 'Discount' },
  advance: { variant: 'success', label: 'Advance' },
};

const TAB_ITEMS = [
  { id: 'ledger', label: 'WIP Ledger' },
  { id: 'realization', label: 'Realization' },
  { id: 'staff-profitability', label: 'Staff Profitability' },
  { id: 'client-profitability', label: 'Client Profitability' },
];

/**
 * WipLedgerPage — WIP management dashboard with ledger, write-up/write-down,
 * realization metrics, and profitability views.
 * Supports global team filtering across all tabs.
 * Requirements: 17.1, 17.4
 */
export function WipLedgerPage() {
  const { activeTab, onChange: setActiveTab } = useTabs('ledger');
  const [teamId, setTeamId] = useState('');
  const { data: teams } = useTeams();

  const teamOptions = useMemo(
    () => (teams ?? []).map((t) => ({ value: t.id, label: t.name })),
    [teams],
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Work In Progress</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Track WIP balances, realization rates, and profitability metrics
          </p>
        </div>
        <div className="w-56">
          <Select
            label="Team"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
            options={teamOptions}
            placeholder="All Teams"
          />
        </div>
      </div>

      {/* Tab Navigation */}
      <Tabs tabs={TAB_ITEMS} activeTab={activeTab} onChange={setActiveTab} />

      {/* Tab Panels */}
      <TabPanel tabId="ledger" activeTab={activeTab}>
        <WipLedgerTab teamId={teamId || undefined} />
      </TabPanel>
      <TabPanel tabId="realization" activeTab={activeTab}>
        <RealizationTab teamId={teamId || undefined} />
      </TabPanel>
      <TabPanel tabId="staff-profitability" activeTab={activeTab}>
        <StaffProfitabilityTab teamId={teamId || undefined} />
      </TabPanel>
      <TabPanel tabId="client-profitability" activeTab={activeTab}>
        <ClientProfitabilityTab teamId={teamId || undefined} />
      </TabPanel>
    </div>
  );
}

// ─── WIP Ledger Tab ─────────────────────────────────────────────────────────

function WipLedgerTab({ teamId }: { teamId?: string }) {
  const [jobFilter, setJobFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [entryType, setEntryType] = useState<WipEntryType | ''>('');
  const [sort, setSort] = useState<SortState>({ column: 'createdAt', direction: 'desc' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [writeModalOpen, setWriteModalOpen] = useState(false);
  const [writeModalType, setWriteModalType] = useState<'up' | 'down'>('up');

  const params: WipLedgerParams = useMemo(
    () => ({
      page,
      pageSize,
      jobId: jobFilter || undefined,
      teamId,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      entryType: entryType || undefined,
    }),
    [page, pageSize, jobFilter, teamId, dateFrom, dateTo, entryType],
  );

  const { data, isLoading } = useWipLedger(params);

  const columns: ColumnDef<WipEntry>[] = useMemo(
    () => [
      {
        id: 'createdAt',
        header: 'Date',
        sortable: true,
        cell: (row) => (
          <span className="text-gray-700 dark:text-gray-300">
            {new Date(row.createdAt).toLocaleDateString('en-CA')}
          </span>
        ),
      },
      {
        id: 'jobTitle',
        header: 'Job',
        accessorKey: 'jobTitle',
        sortable: true,
      },
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
        sortable: true,
      },
      {
        id: 'entryType',
        header: 'Type',
        sortable: true,
        cell: (row) => {
          const badge = ENTRY_TYPE_BADGE[row.entryType];
          // For these types, use green when positive (applied) and red when negative (reversed)
          const amountBasedTypes: WipEntryType[] = [
            'credit_note',
            'write_off',
            'discount',
            'advance',
          ];
          const variant = amountBasedTypes.includes(row.entryType)
            ? row.amount >= 0
              ? 'success'
              : 'danger'
            : badge.variant;
          return (
            <Badge variant={variant} size="sm">
              {badge.label}
            </Badge>
          );
        },
      },
      {
        id: 'description',
        header: 'Description',
        accessorKey: 'description',
      },
      {
        id: 'hours',
        header: 'Hrs/Qty',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span className="text-gray-700 dark:text-gray-300">
            {row.hours != null ? row.hours.toFixed(2) : '—'}
          </span>
        ),
      },
      {
        id: 'amount',
        header: 'Amount',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span
            className={
              row.amount >= 0
                ? 'font-medium text-gray-900 dark:text-gray-100'
                : 'font-medium text-red-600 dark:text-red-400'
            }
          >
            ${row.amount.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'runningBalance',
        header: 'Balance',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            ${row.runningBalance.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
    ],
    [],
  );

  const pagination: PaginationState | undefined = data
    ? { page: data.page, pageSize: data.pageSize, total: data.total }
    : undefined;

  const handlePageChange = useCallback((newPage: number) => setPage(newPage), []);
  const handlePageSizeChange = useCallback((newSize: number) => {
    setPageSize(newSize);
    setPage(1);
  }, []);
  const handleSortChange = useCallback((newSort: SortState) => {
    setSort(newSort);
    setPage(1);
  }, []);

  const openWriteUp = useCallback(() => {
    setWriteModalType('up');
    setWriteModalOpen(true);
  }, []);

  const openWriteDown = useCallback(() => {
    setWriteModalType('down');
    setWriteModalOpen(true);
  }, []);

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      {data && (
        <Card padding="sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total WIP Balance</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                ${data.totalWipBalance.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={openWriteUp}>
                Write-Up
              </Button>
              <Button variant="outline" size="sm" onClick={openWriteDown}>
                Write-Down
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-48">
            <Input
              label="Job ID"
              value={jobFilter}
              onChange={(e) => {
                setJobFilter(e.target.value);
                setPage(1);
              }}
              placeholder="Filter by job..."
            />
          </div>
          <div className="w-40">
            <Input
              label="From"
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-40">
            <Input
              label="To"
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="w-40">
            <Select
              label="Entry Type"
              value={entryType}
              onChange={(e) => {
                setEntryType(e.target.value as WipEntryType | '');
                setPage(1);
              }}
              options={[
                { value: 'time', label: 'Time' },
                { value: 'write_up', label: 'Write-Up' },
                { value: 'write_down', label: 'Write-Down' },
                { value: 'invoice', label: 'Invoice' },
                { value: 'reversal', label: 'Reversal' },
                { value: 'credit_note', label: 'Credit Note' },
                { value: 'write_off', label: 'Write-Off' },
                { value: 'discount', label: 'Discount' },
                { value: 'advance', label: 'Advance' },
              ]}
              placeholder="All types"
            />
          </div>
          {(jobFilter || dateFrom || dateTo || entryType) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setJobFilter('');
                setDateFrom('');
                setDateTo('');
                setEntryType('');
                setPage(1);
              }}
            >
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      {/* WIP Table */}
      <DataTable<WipEntry>
        data={data?.entries ?? []}
        columns={columns}
        keyExtractor={(row) => row.id}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        loading={isLoading}
        emptyMessage="No WIP entries found."
      />

      {/* Write-Up / Write-Down Modal */}
      <WriteAdjustmentModal
        open={writeModalOpen}
        type={writeModalType}
        onClose={() => setWriteModalOpen(false)}
      />
    </div>
  );
}

// ─── Write-Up / Write-Down Modal ────────────────────────────────────────────

interface WriteAdjustmentModalProps {
  open: boolean;
  type: 'up' | 'down';
  onClose: () => void;
}

function WriteAdjustmentModal({ open, type, onClose }: WriteAdjustmentModalProps) {
  const [jobId, setJobId] = useState('');
  const [jobLabel, setJobLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [showJobSearch, setShowJobSearch] = useState(false);

  const writeUp = useWriteUp();
  const writeDown = useWriteDown();

  const isSubmitting = writeUp.isPending || writeDown.isPending;

  const handleJobSelected = (job: JobSearchResult) => {
    setJobId(job.id);
    setJobLabel(`${job.title} – ${job.clientName}`);
    setShowJobSearch(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!jobId) {
      setError('Job is required');
      return;
    }
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      setError('Amount must be a positive number');
      return;
    }
    if (!description.trim()) {
      setError('Description is required');
      return;
    }

    const payload = {
      jobId,
      amount: numAmount,
      description: description.trim(),
    };

    try {
      if (type === 'up') {
        await writeUp.mutateAsync(payload);
      } else {
        await writeDown.mutateAsync(payload);
      }
      handleClose();
    } catch {
      setError('Failed to submit adjustment. Please try again.');
    }
  };

  const handleClose = () => {
    setJobId('');
    setJobLabel('');
    setAmount('');
    setDescription('');
    setError('');
    onClose();
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        title={type === 'up' ? 'Write-Up Adjustment' : 'Write-Down Adjustment'}
        description={
          type === 'up'
            ? 'Increase WIP value before invoice finalization'
            : 'Decrease WIP value before invoice finalization'
        }
        size="md"
      >
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Job
            </label>
            <button
              type="button"
              onClick={() => setShowJobSearch(true)}
              className="flex w-full items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-brand-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-brand-400"
            >
              <span className={jobLabel ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400'}>
                {jobLabel || 'Search & select a job...'}
              </span>
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </button>
          </div>
          <Input
            label="Amount ($)"
            type="number"
            min="0.01"
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
          />
          <Input
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Reason for adjustment"
          />
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Submitting...'
                : `Submit ${type === 'up' ? 'Write-Up' : 'Write-Down'}`}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Job Search Modal — rendered outside parent Modal to avoid z-index/overflow clipping */}
      {showJobSearch && (
        <JobSearchModal
          onSelect={handleJobSelected}
          onClose={() => setShowJobSearch(false)}
          title={`Select Job for ${type === 'up' ? 'Write-Up' : 'Write-Down'}`}
          defaultStatus=""
        />
      )}
    </>
  );
}

// ─── Realization Tab ────────────────────────────────────────────────────────

function RealizationTab({ teamId }: { teamId?: string }) {
  const { data, isLoading } = useRealization({ teamId });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading">
        <p className="text-gray-500 dark:text-gray-400">Loading realization data...</p>
      </div>
    );
  }

  if (!data) {
    return (
      <p className="py-8 text-center text-gray-500 dark:text-gray-400">
        No realization data available.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card padding="md">
          <CardHeader title="Total WIP Value" />
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${data.totalWipValue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card padding="md">
          <CardHeader title="Total Invoiced" />
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              ${data.totalInvoicedValue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
            </p>
          </CardContent>
        </Card>
        <Card padding="md">
          <CardHeader title="Realization Rate" />
          <CardContent>
            <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              {data.realizationRate.toFixed(1)}%
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Per-Job Realization Breakdown */}
      {data.byPeriod.length > 0 && (
        <Card padding="md">
          <CardHeader title="Realization by Job" />
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm" role="table">
                <thead>
                  <tr className="border-b border-border dark:border-border-dark">
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Job</th>
                    <th className="pb-2 font-medium text-gray-500 dark:text-gray-400">Client</th>
                    <th className="pb-2 text-right font-medium text-gray-500 dark:text-gray-400">
                      WIP Value
                    </th>
                    <th className="pb-2 text-right font-medium text-gray-500 dark:text-gray-400">
                      Invoiced
                    </th>
                    <th className="pb-2 text-right font-medium text-gray-500 dark:text-gray-400 w-48">
                      Realization
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.byPeriod.map((item) => (
                    <tr
                      key={item.jobId}
                      className="border-b border-border/50 dark:border-border-dark/50"
                    >
                      <td className="py-3 font-medium text-gray-900 dark:text-gray-100">
                        {item.jobTitle}
                      </td>
                      <td className="py-3 text-gray-600 dark:text-gray-400">{item.clientName}</td>
                      <td className="py-3 text-right text-gray-700 dark:text-gray-300">
                        ${item.wipValue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 text-right text-gray-700 dark:text-gray-300">
                        ${item.invoicedValue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                            <div
                              className={`h-full rounded-full transition-all ${
                                item.realizationRate >= 90
                                  ? 'bg-green-500'
                                  : item.realizationRate >= 70
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                              }`}
                              style={{ width: `${Math.min(item.realizationRate, 100)}%` }}
                            />
                          </div>
                          <span
                            className={`min-w-[3.5rem] text-sm font-medium ${
                              item.realizationRate >= 90
                                ? 'text-green-600 dark:text-green-400'
                                : item.realizationRate >= 70
                                  ? 'text-yellow-600 dark:text-yellow-400'
                                  : 'text-red-600 dark:text-red-400'
                            }`}
                          >
                            {item.realizationRate.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Staff Profitability Tab ────────────────────────────────────────────────

function StaffProfitabilityTab({ teamId }: { teamId?: string }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useMemo(
    () => ({
      teamId,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [teamId, dateFrom, dateTo],
  );

  const { data, isLoading } = useStaffProfitability(params);

  const columns: ColumnDef<StaffProfitability>[] = useMemo(
    () => [
      {
        id: 'staffName',
        header: 'Staff Member',
        accessorKey: 'staffName',
        sortable: true,
      },
      {
        id: 'totalHours',
        header: 'Total Hours',
        sortable: true,
        align: 'right',
        cell: (row) => row.totalHours.toFixed(1),
      },
      {
        id: 'billableHours',
        header: 'Billable Hours',
        sortable: true,
        align: 'right',
        cell: (row) => row.billableHours.toFixed(1),
      },
      {
        id: 'utilizationRate',
        header: 'Utilization',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <Badge
            variant={
              row.utilizationRate >= 70
                ? 'success'
                : row.utilizationRate >= 50
                  ? 'warning'
                  : 'danger'
            }
            size="sm"
          >
            {row.utilizationRate.toFixed(1)}%
          </Badge>
        ),
      },
      {
        id: 'totalCost',
        header: 'Cost',
        sortable: true,
        align: 'right',
        cell: (row) => `$${row.totalCost.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
      },
      {
        id: 'billedRevenue',
        header: 'Revenue',
        sortable: true,
        align: 'right',
        cell: (row) =>
          `$${row.billedRevenue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
      },
      {
        id: 'profit',
        header: 'Profit',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span
            className={
              row.profit >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }
          >
            ${row.profit.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'profitMargin',
        header: 'Margin',
        sortable: true,
        align: 'right',
        cell: (row) => `${row.profitMargin.toFixed(1)}%`,
      },
    ],
    [],
  );

  const [sort, setSort] = useState<SortState>({ column: 'staffName', direction: 'asc' });

  return (
    <div className="space-y-4">
      {/* Date filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-40">
            <Input
              label="From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="w-40">
            <Input
              label="To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear dates
            </Button>
          )}
        </div>
      </Card>

      <DataTable<StaffProfitability>
        data={data ?? []}
        columns={columns}
        keyExtractor={(row) => row.userId}
        sort={sort}
        onSortChange={setSort}
        loading={isLoading}
        emptyMessage="No staff profitability data available for the selected period."
      />
    </div>
  );
}

// ─── Client Profitability Tab ───────────────────────────────────────────────

function ClientProfitabilityTab({ teamId }: { teamId?: string }) {
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const params = useMemo(
    () => ({
      teamId,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    }),
    [teamId, dateFrom, dateTo],
  );

  const { data, isLoading } = useClientProfitability(params);

  const columns: ColumnDef<ClientProfitability>[] = useMemo(
    () => [
      {
        id: 'clientName',
        header: 'Client',
        accessorKey: 'clientName',
        sortable: true,
      },
      {
        id: 'totalHours',
        header: 'Total Hours',
        sortable: true,
        align: 'right',
        cell: (row) => row.totalHours.toFixed(1),
      },
      {
        id: 'totalCost',
        header: 'Cost',
        sortable: true,
        align: 'right',
        cell: (row) => `$${row.totalCost.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
      },
      {
        id: 'billedRevenue',
        header: 'Billed',
        sortable: true,
        align: 'right',
        cell: (row) =>
          `$${row.billedRevenue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
      },
      {
        id: 'writeUps',
        header: 'Write-Ups',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span className="text-green-600 dark:text-green-400">
            ${row.writeUps.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'writeDowns',
        header: 'Write-Downs',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span className="text-red-600 dark:text-red-400">
            ${row.writeDowns.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'netRevenue',
        header: 'Net Revenue',
        sortable: true,
        align: 'right',
        cell: (row) => `$${row.netRevenue.toLocaleString('en-CA', { minimumFractionDigits: 2 })}`,
      },
      {
        id: 'profit',
        header: 'Profit',
        sortable: true,
        align: 'right',
        cell: (row) => (
          <span
            className={
              row.profit >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400'
            }
          >
            ${row.profit.toLocaleString('en-CA', { minimumFractionDigits: 2 })}
          </span>
        ),
      },
      {
        id: 'profitMargin',
        header: 'Margin',
        sortable: true,
        align: 'right',
        cell: (row) => `${row.profitMargin.toFixed(1)}%`,
      },
    ],
    [],
  );

  const [sort, setSort] = useState<SortState>({ column: 'clientName', direction: 'asc' });

  return (
    <div className="space-y-4">
      {/* Date filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-40">
            <Input
              label="From"
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="w-40">
            <Input
              label="To"
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
          {(dateFrom || dateTo) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
            >
              Clear dates
            </Button>
          )}
        </div>
      </Card>

      <DataTable<ClientProfitability>
        data={data ?? []}
        columns={columns}
        keyExtractor={(row) => row.clientId}
        sort={sort}
        onSortChange={setSort}
        loading={isLoading}
        emptyMessage="No client profitability data available for the selected period."
      />
    </div>
  );
}
